import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Lenis from "lenis";
import { SquareTerminal, X } from "lucide-react";
import Wallpaper from "@/components/desktop/Wallpaper";
import MenuBar from "@/components/desktop/MenuBar";
import Dock from "@/components/desktop/Dock";
import GreetingModal from "@/components/desktop/GreetingModal";
import Spotlight from "@/components/desktop/Spotlight";
import Window from "@/components/window/Window";
import Terminal from "@/components/terminal/Terminal";
import { APPS } from "@/data/windowRegistry";
import { useWindows } from "@/context/WindowContext";
import { useVisitor } from "@/context/VisitorContext";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Toaster } from "@/components/ui/sonner";
import { EASE } from "@/components/ui/Reveal";

function MobileDesktop() {
  const [sheetOpen, setSheetOpen] = useState(false);
  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  const sections = APPS.filter((a) => a.id !== "terminal");

  useEffect(() => {
    if (!scrollRef.current || !contentRef.current) return undefined;
    const lenis = new Lenis({ wrapper: scrollRef.current, content: contentRef.current, duration: 1.1 });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="relative h-full w-full" data-testid="desktop-mobile">
      <Wallpaper />
      <MenuBar activeTitle="mobile workspace" />
      <div ref={scrollRef} className="absolute inset-x-0 bottom-0 top-9 overflow-y-auto">
        <div ref={contentRef} className="space-y-5 px-3 pb-36 pt-3">
          {sections.map((a) => (
            <section key={a.id} className="glass overflow-hidden rounded-xl" data-testid={`mobile-section-${a.id}`}>
              <div className="flex items-center gap-2 border-b border-white/10 bg-white/[.05] px-3 py-2">
                <a.icon size={12} className="text-slate-400" />
                <p className="font-mono text-[11px] text-slate-400">{a.title}</p>
              </div>
              <div className="p-3">
                <a.component />
              </div>
            </section>
          ))}
        </div>
      </div>

      <button
        onClick={() => setSheetOpen(true)}
        data-testid="mobile-terminal-fab"
        aria-label="Open terminal"
        className="fixed bottom-24 right-4 z-[650] flex h-12 w-12 items-center justify-center rounded-full bg-sonoma shadow-lg shadow-blue-900/50 ring-1 ring-white/20 transition-transform duration-150 active:scale-95"
      >
        <SquareTerminal size={20} className="text-white" />
      </button>

      <AnimatePresence>
        {sheetOpen && (
          <motion.div
            className="fixed inset-0 z-[700] bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSheetOpen(false)}
            data-testid="mobile-terminal-overlay"
          >
            <motion.div
              className="glass absolute inset-x-2 bottom-3 top-14 flex flex-col overflow-hidden rounded-xl"
              initial={{ y: 48, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 48, opacity: 0 }}
              transition={{ duration: 0.28, ease: EASE }}
              onClick={(e) => e.stopPropagation()}
              data-testid="mobile-terminal-sheet"
            >
              <div className="flex h-9 shrink-0 items-center justify-between border-b border-white/10 bg-white/[.05] px-3">
                <p className="font-mono text-[11px] text-slate-400">zsh — anish@portfolio</p>
                <button onClick={() => setSheetOpen(false)} data-testid="mobile-terminal-close" aria-label="Close terminal" className="text-slate-400 hover:text-white">
                  <X size={15} />
                </button>
              </div>
              <div className="min-h-0 flex-1">
                <Terminal />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dock />
      <GreetingModal />
      <Spotlight />
      <Toaster position="top-center" theme="dark" />
    </div>
  );
}

export default function Desktop() {
  const isMobile = useIsMobile();
  const { wins, activeId } = useWindows();
  const { name, promptOpen, setPromptOpen } = useVisitor();

  useEffect(() => {
    if (!name) {
      const t = setTimeout(() => setPromptOpen(true), 1100);
      return () => clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isMobile) return <MobileDesktop />;

  const activeApp = APPS.find((a) => a.id === activeId);
  const visible = APPS.filter((a) => wins[a.id] && wins[a.id].open && !wins[a.id].minimized);

  return (
    <div className="relative h-full w-full" data-testid="desktop">
      <Wallpaper />
      <MenuBar activeTitle={activeApp ? activeApp.title : undefined} />
      <main className="absolute inset-0" style={{ top: 36 }} data-testid="desktop-canvas">
        <AnimatePresence>
          {visible.map((a) => (
            <Window key={a.id} app={a} win={wins[a.id]}>
              <a.component />
            </Window>
          ))}
        </AnimatePresence>
      </main>
      <Dock />
      <GreetingModal />
      <Spotlight />
      <Toaster position="top-right" theme="dark" />
    </div>
  );
}
